// Nagłówek ekranu głównego: awatar, imię i „Poziom” (dotknięcie otwiera „Mój wygląd”) oraz znaczek zapisu postępu na serwerze — widoczny tylko, gdy zapis trwa albo coś nie działa (chmurki „zapisane” nie pokazujemy).
// „Zmień profil” jest na dole ekranu, obok „Dla rodzica” (renderSwitchProfile) — dziecko nie zmienia profilu przypadkiem.
// Napisy idą za przełącznikiem EN/PL (podpowiedzi, „Poziom”, znaczek zapisu).
import { config } from '../config.ts';
import type { Pair } from '../lib/i18n.ts';
import { syncBadge } from '../lib/syncView.ts';
import { renderLookScreen } from './accountScreens.ts';
import { avatarEl } from './avatars.ts';
import type { AppCtx } from './ctx.ts';
import { button, h } from './dom.ts';
import { langOf, txAttr, txText } from './i18n.ts';
import { liveLang } from './i18nLive.ts';

const T = {
  chip: { en: 'Change avatar and name', pl: 'Zmień awatar i imię' },
  myLook: { en: 'My look', pl: 'Mój wygląd' },
  switchProfile: { en: 'Switch profile', pl: 'Zmień profil' },
} as const satisfies Record<string, Pair>;

export function renderProfileBar(ctx: AppCtx): HTMLElement | null {
  const acc = ctx.account;
  if (!acc) return null;
  const family = acc.mode() === 'family';
  const profile = acc.profile();

  const badge = h('span', { class: 'sync-badge', role: 'img', 'data-testid': 'sync-badge' });
  const paint = (): void => {
    const b = syncBadge(acc.syncState(), langOf(ctx));
    badge.textContent = b.icon;
    badge.setAttribute('aria-label', b.label);
    badge.setAttribute('title', b.label);
    badge.hidden = b.quiet; // „zapisane” = bez znaczka (zostaje w DOM, żeby testy i czytniki widziały stan); ostrzeżenia i zapis w toku widać
  };
  paint();
  if (family) {
    // Ekran główny jest przerysowywany od zera, więc nasłuch odpinamy, gdy znaczka nie ma już na stronie.
    const off = acc.onSync(() => {
      if ((badge as { isConnected?: boolean }).isConnected === false) off();
      else paint();
    });
    liveLang(ctx, badge, paint); // opis znaczka idzie za przełącznikiem EN/PL
  }

  const chip = txAttr(ctx, h('button', { type: 'button', class: 'profile-chip', 'data-testid': 'profile-chip', onclick: () => renderLookScreen(ctx) },
    avatarEl(acc.look(), 'md', ctx),
    h('span', { class: 'pc-text' },
      profile ? h('span', { class: 'pc-name', 'data-testid': 'pc-name' }, profile.name) : txText(ctx, h('span', { class: 'pc-name', 'data-testid': 'pc-name' }), T.myLook),
      txText(ctx, h('span', { class: 'pc-level', 'data-testid': 'pc-level' }), { en: `Level ${config.ui.level}`, pl: `Poziom ${config.ui.level}` }),
    ),
  ), 'title', T.chip);
  return h('div', { class: 'profile-bar', 'data-testid': 'profile-bar' }, chip, family && badge);
}

/** „Zmień profil”: zawsze, gdy są konta rodziny (patch-34) — kończy sesję dziecka i wraca do „Kto dziś się uczy?” (z PIN-em profilu, jeśli ma). Po nim znów jest powitanie. */
export function renderSwitchProfile(ctx: AppCtx): HTMLElement | null {
  const acc = ctx.account;
  if (!acc || acc.mode() !== 'family') return null;
  return txText(ctx, button('', () => void acc.leave().then(() => ctx.goHome()), { class: 'btn link', testid: 'switch-profile' }), T.switchProfile);
}
